(() => {
  const STORAGE_KEY = 'healthExpressWishlistV1';

  function readWishlist() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? [...new Set(stored.filter((item) => typeof item === 'string' && item))] : [];
    } catch (_error) {
      return [];
    }
  }

  function writeWishlist(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...new Set(items)]));
    document.dispatchEvent(new CustomEvent('wishlist:changed'));
  }

  function localizedVendor(vendor) {
    const language = document.documentElement.lang || '';
    const english = {
      '大善': 'DaiZen',
      '康檸C': 'CalVive C',
      '康鈣C': 'CalVive C',
      '博士倫': 'Bausch + Lomb',
      '賢者的食卓': 'Otsuka Kenja-no-Shokutaku',
    };
    const simplifiedChinese = {
      '康檸C': '康柠C',
      '康鈣C': '康钙C',
      '博士倫': '博士伦',
      '賢者的食卓': '贤者的食卓',
    };
    if (language.toLowerCase().startsWith('en')) return english[vendor] || vendor;
    if (language.toLowerCase() === 'zh-cn') return simplifiedChinese[vendor] || vendor;
    return vendor;
  }

  function updateHeader() {
    const count = readWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count === 0;
    });
    document.querySelectorAll('[data-wishlist-link]').forEach((link) => {
      const countLabel = link.dataset.labelCount || 'Wishlist, {{ count }} products';
      link.setAttribute('aria-label', count ? countLabel.replace('{{ count }}', String(count)) : (link.dataset.labelEmpty || 'Wishlist'));
    });
  }

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-wishlist-label]');
      this.handle = this.dataset.productHandle;
      if (!this.button || !this.handle) return;
      this.button.addEventListener('click', () => this.toggle());
      document.addEventListener('wishlist:changed', () => this.render());
      this.render();
    }

    toggle() {
      const items = readWishlist();
      const index = items.indexOf(this.handle);
      if (index >= 0) items.splice(index, 1);
      else items.push(this.handle);
      writeWishlist(items);
    }

    render() {
      const saved = readWishlist().includes(this.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.setAttribute('aria-label', saved ? this.dataset.ariaRemove : this.dataset.ariaAdd);
      this.classList.toggle('is-saved', saved);
      if (this.label) this.label.textContent = saved ? this.dataset.labelAdded : this.dataset.labelAdd;
    }
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.status = this.querySelector('[data-wishlist-status]');
      document.addEventListener('wishlist:changed', () => this.render());
      this.render();
    }

    async render() {
      if (!this.grid || !this.empty) return;
      const handles = readWishlist();
      this.grid.replaceChildren();
      this.empty.hidden = handles.length > 0;
      if (!handles.length) {
        if (this.status) this.status.textContent = this.dataset.statusEmpty;
        return;
      }

      if (this.status) this.status.textContent = this.dataset.statusLoading.replace('{{ count }}', String(handles.length));
      const products = await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`);
            return response.ok ? await response.json() : null;
          } catch (_error) {
            return null;
          }
        })
      );

      products.filter(Boolean).forEach((product) => this.grid.append(this.createCard(product)));
      const loaded = products.filter(Boolean).length;
      if (this.status) this.status.textContent = this.dataset.statusLoaded.replace('{{ count }}', String(loaded));
    }

    createCard(product) {
      const article = document.createElement('article');
      article.className = 'wishlist-card';

      const link = document.createElement('a');
      link.className = 'wishlist-card__image-link';
      link.href = product.url;
      link.setAttribute('aria-label', product.title);

      if (product.featured_image) {
        const image = document.createElement('img');
        image.className = 'wishlist-card__image';
        image.src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 600;
        image.height = 600;
        link.append(image);
      }

      const body = document.createElement('div');
      body.className = 'wishlist-card__body';
      if (product.vendor) {
        const vendor = document.createElement('p');
        vendor.className = 'wishlist-card__vendor';
        vendor.textContent = localizedVendor(product.vendor);
        body.append(vendor);
      }

      const title = document.createElement('h2');
      title.className = 'wishlist-card__title';
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      title.append(titleLink);
      body.append(title);

      const availableVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      const price = document.createElement('p');
      price.className = 'wishlist-card__price';
      price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'zh-HK', { style: 'currency', currency: 'HKD' }).format((availableVariant?.price || 0) / 100);
      body.append(price);

      const actions = document.createElement('div');
      actions.className = 'wishlist-card__actions';
      const view = document.createElement('a');
      view.className = 'button button--primary';
      view.href = product.url;
      view.textContent = availableVariant?.available ? this.dataset.labelView : this.dataset.labelViewSoldOut;
      const remove = document.createElement('button');
      remove.className = 'button button--secondary';
      remove.type = 'button';
      remove.textContent = this.dataset.labelRemove;
      remove.addEventListener('click', () => writeWishlist(readWishlist().filter((handle) => handle !== product.handle)));
      actions.append(view, remove);
      body.append(actions);

      article.append(link, body);
      return article;
    }
  }

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
  document.addEventListener('DOMContentLoaded', updateHeader, { once: true });
  document.addEventListener('wishlist:changed', updateHeader);
})();
